import { Capacitor } from '@capacitor/core';
import {
  RESOURCE_TYPES,
  emptyResource,
  type ResourceDocument,
  type SyncOperation
} from '../domain/resources';
import { IndexedDbAdapter } from '../data/indexeddb-adapter';
import type { LocalDatabase } from '../data/local-database';
import { MemoryDatabase } from '../data/local-database';
import { NativeSqliteAdapter } from '../data/native-sqlite-adapter';

export function isNativePlatform(): boolean {
  return Capacitor.isNativePlatform();
}

export async function createLocalDatabase(): Promise<LocalDatabase> {
  if (isNativePlatform()) {
    try {
      const sqlite = new NativeSqliteAdapter();
      await sqlite.initialize();
      await recoverNativeFallback(sqlite);
      return sqlite;
    } catch (error) {
      console.error('[storage] Native SQLite failed, falling back to IndexedDB:', error);
    }
  }

  try {
    const indexedDb = new IndexedDbAdapter();
    await indexedDb.initialize();
    return indexedDb;
  } catch (error) {
    console.error('[storage] IndexedDB failed; changes will only live in memory:', error);
    const memory = new MemoryDatabase();
    await memory.initialize();
    return memory;
  }
}

async function recoverNativeFallback(target: LocalDatabase): Promise<void> {
  if (await target.getMetadata('migration:native-indexeddb-fallback-v1')) return;

  let resources: ResourceDocument[];
  let operations: SyncOperation[];
  try {
    const source = new IndexedDbAdapter();
    await source.initialize();
    resources = await source.listResources();
    operations = await source.listOperations();
  } catch (error) {
    console.info('[storage] No native IndexedDB fallback to recover:', error);
    await target.setMetadata('migration:native-indexeddb-fallback-v1', new Date().toISOString());
    return;
  }

  const targetResources = new Map(
    (await target.listResources()).map(resource => [resource.type, resource])
  );
  const sourceResources = new Map(resources.map(resource => [resource.type, resource]));
  for (const type of RESOURCE_TYPES) {
    const typeOperations = operations.filter(operation => operation.resourceType === type);
    const sourceResource = sourceResources.get(type);
    if (sourceResource || typeOperations.length) {
      await target.commit(
        targetResources.get(type) || sourceResource || emptyResource(type),
        typeOperations
      );
    }
  }

  await target.setMetadata('migration:native-indexeddb-fallback-v1', new Date().toISOString());
}
